import React, { useState, useEffect, useCallback, useRef } from 'react';
import { investigationService, knownAddressService } from '../services/api';
import '../styles/TagsPanel.css';

const TAG_OPTIONS = [
  'suspicious', 'known-address', 'exchange', 'contract', 'mixer',
  'sanctioned', 'bridge', 'defi', 'verified', 'whale', 'bot', 'scam', 'other',
];

const TAG_COLORS = {
  'suspicious':    '#FF9800',
  'known-address': '#4CAF50',
  'exchange':      '#f59e0b',
  'contract':      '#a855f7',
  'mixer':         '#ef4444',
  'sanctioned':    '#dc2626',
  'bridge':        '#06b6d4',
  'defi':          '#10b981',
  'verified':      '#8BC34A',
  'whale':         '#FFC107',
  'bot':           '#00BCD4',
  'scam':          '#F44336',
  'other':         '#757575',
};

// Map known registry address_type → nearest tag
const KNOWN_TYPE_TO_TAG = {
  contract:   'contract',
  exchange:   'exchange',
  mixer:      'mixer',
  sanctioned: 'sanctioned',
  bridge:     'bridge',
  defi:       'defi',
  other:      'known-address',
};

export default function TagsPanel({ investigationId }) {
  const [tags, setTags]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newTag, setNewTag]         = useState({ address: '', tag: 'suspicious', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [lookingUp, setLookingUp]   = useState(false);
  const [knownHint, setKnownHint]   = useState(null);   // { name, symbol, address_type, color }
  const debounceRef = useRef(null);

  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      const res = await investigationService.getTags(investigationId);
      setTags(res.data);
    } catch (e) {
      console.error('Failed to load tags:', e);
    } finally {
      setLoading(false);
    }
  }, [investigationId]);

  useEffect(() => { loadTags(); }, [loadTags]);

  // ── Auto-lookup address against known registry (debounced 600ms) ──
  const handleAddressChange = (val) => {
    setNewTag(t => ({ ...t, address: val }));
    setKnownHint(null);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 10) return;
    debounceRef.current = setTimeout(async () => {
      try {
        setLookingUp(true);
        const res = await knownAddressService.lookup(val.trim());
        if (res.data) {
          const k = res.data;
          setKnownHint(k);
          // Auto-fill tag and notes if still at defaults
          setNewTag(t => ({
            ...t,
            tag:   KNOWN_TYPE_TO_TAG[k.address_type] || 'known-address',
            notes: t.notes || `${k.name}${k.symbol ? ` (${k.symbol})` : ''}`,
          }));
        }
      } catch {
        // 404 → not in registry, silent
      } finally {
        setLookingUp(false);
      }
    }, 600);
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag.address.trim() || !newTag.tag.trim()) return;
    try {
      setSubmitting(true);
      await investigationService.addTag(investigationId, newTag);
      setNewTag({ address: '', tag: 'suspicious', notes: '' });
      setKnownHint(null);
      loadTags();
    } catch (e) {
      console.error('Failed to add tag:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await investigationService.deleteTag(investigationId, tagId);
      loadTags();
    } catch (e) {
      console.error('Failed to delete tag:', e);
    }
  };

  return (
    <div className="tags-panel">
      <h3>🏷️ Address Tags</h3>

      <form onSubmit={handleAddTag} className="tag-form">

        {/* Address input with live registry lookup */}
        <div className="tag-addr-wrap">
          <input
            type="text"
            value={newTag.address}
            onChange={e => handleAddressChange(e.target.value)}
            placeholder="Paste address to tag…"
            className="tag-input tag-mono"
          />
          {lookingUp && <span className="tag-looking-up">🔍 checking registry…</span>}
        </div>

        {/* Known registry hint banner */}
        {knownHint && (
          <div className="tag-known-hint" style={{ borderColor: knownHint.color }}>
            <span className="tag-known-dot" style={{ background: knownHint.color }} />
            <div className="tag-known-info">
              <strong style={{ color: knownHint.color }}>{knownHint.name}</strong>
              {knownHint.symbol && <span className="tag-known-symbol">{knownHint.symbol}</span>}
              <span className="tag-known-type">{knownHint.address_type}</span>
            </div>
            <span className="tag-known-label">Found in registry — tag pre-filled</span>
          </div>
        )}

        <div className="tag-row">
          <select
            value={newTag.tag}
            onChange={e => setNewTag(t => ({ ...t, tag: e.target.value }))}
            className="tag-select"
            style={{ borderColor: TAG_COLORS[newTag.tag] + '66', color: TAG_COLORS[newTag.tag] }}
          >
            {TAG_OPTIONS.map(tag => (
              <option key={tag} value={tag}>{tag}</option>
            ))}
          </select>

          <button
            type="submit"
            disabled={submitting || !newTag.address.trim()}
            className="btn-submit"
          >
            {submitting ? 'Adding…' : '+ Add Tag'}
          </button>
        </div>

        <textarea
          value={newTag.notes}
          onChange={e => setNewTag(t => ({ ...t, notes: e.target.value }))}
          placeholder="Notes (optional)"
          className="tag-notes"
          rows={2}
        />
      </form>

      {loading ? (
        <p className="tags-loading">Loading tags…</p>
      ) : tags.length === 0 ? (
        <p className="empty">No tags yet. Paste an address above to get started.</p>
      ) : (
        <div className="tags-list">
          {tags.map(tag => (
            <div key={tag.id} className="tag-item">
              <div className="tag-header">
                <code className="tag-address">{tag.address}</code>
                <span className="tag-badge" style={{
                  background:   TAG_COLORS[tag.tag] + '22',
                  color:        TAG_COLORS[tag.tag],
                  borderColor:  TAG_COLORS[tag.tag] + '66',
                }}>
                  {tag.tag}
                </span>
              </div>
              {tag.notes && <p className="tag-notes-display">{tag.notes}</p>}
              <div className="tag-footer">
                <span className="tag-date">{new Date(tag.created_at).toLocaleDateString()}</span>
                <button onClick={() => handleDeleteTag(tag.id)} className="btn-delete">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
